/**
 * Manages loading of privacy banner and tracking scripts based on the current privacy choices.
 */

import { MiaPlaza } from "@/Reinforced.Typings";
import OneTrust, { OneTrustCookieGroup } from "@/tracking/OneTrust";
import Amplitude from "@/tracking/Amplitude";
import Unbounce from "@/tracking/Unbounce";
import { Component, Prop, toNative, Vue } from "vue-facing-decorator";
import { type VNode } from "vue";
import GoogleAds, { type GoogleAdsTrackingOptions } from "../../../tracking/GoogleAds";
import FacebookPixel from "@/tracking/FacebookPixel";
import PinterestTag from "@/tracking/PinterestTag";
import IHeartMediaPixel from "@/tracking/IHeartMediaPixel";
import Klaviyo from "@/tracking/Klaviyo";
import Log from "@/utils/Log";

@Component
export class ScriptManager extends Vue {
	@Prop({ required: true }) protected amplitudeOptions!: MiaPlaza.Control.Tracking.IAmplitudeTrackingOptions | null;
	@Prop({ required: true }) protected unbounceOptions!: MiaPlaza.Control.Tracking.IUnbounceTrackingOptions | null;
	@Prop({ required: true }) protected googleAdsOptions!: GoogleAdsTrackingOptions | null;
	@Prop({ required: true }) protected facebookPixelOptions!: MiaPlaza.Control.Tracking.IFacebookPixelTrackingOptions | null;
	@Prop({ required: true }) protected pinterestTagOptions!: MiaPlaza.Control.Tracking.IPinterestTagTrackingOptions | null;
	@Prop({ required: true }) protected iheartMediaPixelOptions!: MiaPlaza.Control.Tracking.IIHeartMediaPixelTrackingOptions | null;
	@Prop({ required: true }) protected klaviyoFormOptions!: MiaPlaza.Control.Tracking.IKlaviyoFormTrackingOptions | null;

	@Prop({ required: true, type: Boolean }) protected childDirected!: boolean;
	@Prop({ required: true, type: Boolean }) protected performanceCookieConsentRequired!: boolean;
	@Prop({ required: true, type: String }) protected onetrustDomainScript!: string;

	protected async created(): Promise<void> {
		if (this.childDirected) {
			await this.initalizeForChildren();
		}
		else {
			await this.initalizeForParents();
		}
	}

	private async initalizeForParents(): Promise<void> {
		if (!this.onetrustDomainScript) {
			await Log.error("OneTrust Domain Script is not configured, cannot load OneTrust Banner for Consent Management.");
			return;
		}

		const amplitude = this.amplitudeOptions ? new Amplitude(this.amplitudeOptions) : null;
		const unbounce = this.unbounceOptions ? new Unbounce(this.unbounceOptions) : null;
		const googleAds = this.googleAdsOptions ? new GoogleAds(this.googleAdsOptions) : null;
		const facebookPixel = this.facebookPixelOptions ? new FacebookPixel(this.facebookPixelOptions) : null;
		const pinterestTag = this.pinterestTagOptions ? new PinterestTag(this.pinterestTagOptions) : null;
		const iHeartMediaPixel = this.iheartMediaPixelOptions ? new IHeartMediaPixel(this.iheartMediaPixelOptions) : null;
		const klaviyo = this.klaviyoFormOptions ? new Klaviyo(this.klaviyoFormOptions) : null;

		const oneTrust = new OneTrust((activeCookieGroups, previousActiveCookieGroups) => {
			// some previous consent has been withdrawn. The easiest here is to reload the page
			if (previousActiveCookieGroups.some((group) => !activeCookieGroups.includes(group))) {
				window.location.reload();
			}

			// consent has been extended, except trivial or initial states, such as no active groups or strictly necessary cookies.
			const trivialInitialState = previousActiveCookieGroups.length === 0 || (previousActiveCookieGroups.length === 1 && previousActiveCookieGroups[0] === OneTrustCookieGroup.StrictlyNecessary);
			if (!trivialInitialState && activeCookieGroups.some((group) => !previousActiveCookieGroups.includes(group))) {
				window.location.reload();
			}

			if (activeCookieGroups.includes(OneTrustCookieGroup.Performance)) {
				if (activeCookieGroups.includes(OneTrustCookieGroup.Targeting)) {
					void amplitude?.load(true);
				}
				else {
					void amplitude?.load(false);
				}
			}

			if (activeCookieGroups.includes(OneTrustCookieGroup.Targeting)) {
				googleAds?.load();
				unbounce?.load();
				facebookPixel?.load();
				pinterestTag?.load();
				iHeartMediaPixel?.load();
				klaviyo?.load();
			}
		});
		oneTrust.load(this.onetrustDomainScript);
	}

	private async initalizeForChildren(): Promise<void> {
		const amplitude = this.amplitudeOptions ? new Amplitude(this.amplitudeOptions) : null;

		if (!this.performanceCookieConsentRequired) {
			await amplitude?.load(false);
		}
	}

	protected render(): VNode[] | undefined { return undefined; }
}

export default toNative(ScriptManager);
